import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import SubscriptionGate from "@/components/SubscriptionGate";
import OfflineBanner from "@/components/OfflineBanner";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { ExerciseTimerProvider } from "@/contexts/ExerciseTimerContext";
import { supabase } from "@/integrations/supabase/client";
import { refreshNotificationsIfNeeded } from "@/lib/notifications";
import { useState, useEffect, lazy, Suspense } from "react";
import Index from "./pages/Index";

const Auth = lazy(() => import("./pages/Auth"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));

const Onboarding = lazy(() => import("./pages/Onboarding"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Scan = lazy(() => import("./pages/Scan"));
const Exercises = lazy(() => import("./pages/Exercises"));
const Recipes = lazy(() => import("./pages/Recipes"));
const Profile = lazy(() => import("./pages/Profile"));
const Hydration = lazy(() => import("./pages/Hydration"));
const Achievements = lazy(() => import("./pages/Achievements"));
const History = lazy(() => import("./pages/History"));

const queryClient = new QueryClient();

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const [termsChecked, setTermsChecked] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  useEffect(() => {
    if (!user) { setTermsChecked(true); return; }
    supabase.from("profiles").select("terms_accepted_at").eq("user_id", user.id).single().then(({ data }) => {
      setNeedsOnboarding(!data?.terms_accepted_at);
      setTermsChecked(true);
    });
    // Auto-refresh notifications on app launch
    refreshNotificationsIfNeeded();
  }, [user]);

  if (loading || !termsChecked) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/auth" />;
  if (needsOnboarding) return <Navigate to="/onboarding" />;
  return <SubscriptionGate>{children}</SubscriptionGate>;
};

const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <ExerciseTimerProvider>
          <PaymentTestModeBanner />
          <OfflineBanner />
          <main>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/oauth/callback" element={<AuthCallback />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/scan" element={<ProtectedRoute><Scan /></ProtectedRoute>} />
            <Route path="/exercises" element={<ProtectedRoute><Exercises /></ProtectedRoute>} />
            <Route path="/recipes" element={<ProtectedRoute><Recipes /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/hydration" element={<ProtectedRoute><Hydration /></ProtectedRoute>} />
            <Route path="/achievements" element={<ProtectedRoute><Achievements /></ProtectedRoute>} />
            <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </main>
          </ExerciseTimerProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
